import { Link, useLocation } from 'react-router';

import { useAuth } from '@/auth/useAuth';
import { useModules } from '@/modules/ModuleContext';
import { locatePath, offeredViews } from './navigation';

/**
 * What the Schedule header keeps in the address: the day, the technician and the search.
 *
 * Board, Map and Jobs show the same slice of the schedule, so these three travel with a person from
 * one to the next. Anything else in the address belongs to the page that put it there (an open job,
 * the lanes shown) and stays behind.
 */
const SCHEDULE_PARAMS = ['date', 'tech', 'q'] as const;

/**
 * The part of an address that a Schedule view hands to the next.
 *
 * @param search the current address's query string, with or without its leading "?"
 * @returns the carried query string with its "?", or an empty string when nothing is chosen
 */
function carriedSchedule(search: string): string {
  const current = new URLSearchParams(search);
  const carried = new URLSearchParams();
  for (const name of SCHEDULE_PARAMS) {
    const value = current.get(name);
    if (value !== null && value !== '') carried.set(name, value);
  }
  const query = carried.toString();
  return query === '' ? '' : `?${query}`;
}

/**
 * The views of the current destination, drawn above the page on the destination's own routes.
 *
 * Links rather than ARIA tabs: each view is a route with its own address, so choosing one
 * navigates, and the current one says so with `aria-current`. Nothing is drawn on a record (a
 * deal, a customer, a job) because the record is the page there, nor when this person may open
 * only one view, since a lone tab is noise.
 *
 * Within Schedule each tab carries the day, technician and search, so moving between Board, Map and
 * Jobs does not lose them. No other destination carries anything.
 *
 * @returns the tab strip, or nothing
 */
export function ViewTabs(): React.JSX.Element | null {
  const { pathname, search } = useLocation();
  const { can } = useAuth();
  const enabled = useModules();
  const located = locatePath(pathname);
  if (!located?.isViewRoute) return null;

  const views = offeredViews(located.destination, { can, enabled });
  if (views.length < 2) return null;

  const carried = located.destination.id === 'schedule' ? carriedSchedule(search) : '';

  return (
    <nav className="fo-viewtabs" aria-label={`${located.destination.label} views`}>
      <ul className="fo-viewtabs__list">
        {views.map((view) => (
          <li key={view.id}>
            <Link
              to={{ pathname: view.paths[0], search: carried }}
              className="fo-viewtabs__tab"
              data-testid={`view-${view.id}`}
              aria-current={view.id === located.view.id ? 'page' : undefined}
            >
              {view.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}
